import { DEFINITIONS } from '../utils/constants';
import { getDistanceUnit, getOpeningHours, isStoreOpenNow, sortStoresByDistance } from '../utils/stores';
import { getTranslations } from '../utils/translations';

/**
 * Virtual list state management
 */
const virtualListState = {
  container: null,
  scrollContainer: null,
  items: [],
  itemHeight: 120, // Estimated average height in pixels
  visibleRange: { start: 0, end: 0 },
  overscan: 3, // Number of items to render outside visible area
  rendered: new Map(), // Cache of rendered elements
  context: null,
  translations: null,
  map: null,
  scrollListener: null, // Store scroll listener for cleanup
  resizeListener: null, // Store resize listener for cleanup
  minVisibleItems: 5, // Minimum items to always render
};

/**
 * @description Renders the inner HTML for a single store list item
 * @param {Object} store - Store data
 * @param {Object} translations - Translation strings
 * @returns {string} HTML string
 */
const renderListItemContent = (store, translations) => {
  const addressParts = [store.postal_code, store.city, store.country].filter(Boolean).join(', ');
  const address =
    store.address && addressParts
      ? `
        <div class="${DEFINITIONS.slug}__list-item-address">
          <p>${store.address}</p>
          <p>${addressParts}</p>
        </div>
      `
      : store.address
        ? `<p class="${DEFINITIONS.slug}__list-item-address">${store.address}</p>`
        : '';

  let today = new Date().getDay();
  today = today === 0 ? 6 : today - 1;

  const openingHoursList = Object.entries(getOpeningHours(store, translations?.store_locator?.store_closed) || {})
    .map(([key, value]) => {
      const dayIndex = parseInt(key.split('-')[0], 10);
      const isToday = dayIndex === today;
      const isOpenNow = isToday ? (isStoreOpenNow(value) ? ' today today-open-now' : ' today') : '';
      return `<li class="${DEFINITIONS.slug}__list-item-opening-hours-day${isOpenNow}">${value}</li>`;
    })
    .join('');

  const openingHours = openingHoursList
    ? `<details class="${DEFINITIONS.slug}__list-item-opening-hours" name="opening-hours"><summary>${translations?.store_locator?.opening_hours}</summary><ul>${openingHoursList}</ul></details>`
    : '';

  const phoneIcon = `<svg class="${DEFINITIONS.slug}__icon"><use href="#icon-phone"></use></svg>`;
  const directionsIcon = `<svg class="${DEFINITIONS.slug}__icon"><use href="#icon-directions"></use></svg>`;

  const storePhone = store.phone
    ? `<a href="tel:${store.phone}" class="${DEFINITIONS.slug}__list-item-phone">${phoneIcon}${store.phone}</a>`
    : '';
  const storeDirections = `<a href="https://www.google.com/maps/dir//${store.latitude},${store.longitude}" target="_blank" class="${DEFINITIONS.slug}__list-item-directions">${directionsIcon}${translations?.store_locator?.get_directions}</a>`;

  return `
    <p class="${DEFINITIONS.slug}__list-item-name">${store.name || ''}</p>
    ${address}
    ${openingHours}
    ${storePhone}
    ${storeDirections}
    ${store.distance ? `<span class="${DEFINITIONS.slug}__list-item-distance">${getDistanceUnit(store.distance)}</span>` : ''}
  `;
};

/**
 * @description Creates a single virtual list item element
 * @param {Object} store - Store data
 * @param {Object} translations - Translation strings
 * @param {Object} map - Google Map instance
 * @param {HTMLElement} context - Section container
 * @returns {HTMLElement} List item element
 */
const createVirtualListItem = (store, translations, map, context) => {
  const li = document.createElement('li');
  li.className = `${DEFINITIONS.slug}__list-item`;
  li.dataset.id = store.uid || store.id;
  li.innerHTML = renderListItemContent(store, translations);

  // Click handler to center map on store
  li.addEventListener('click', (e) => {
    if (!map || !window.google?.maps?.LatLng) return;

    if (['a', 'details', 'summary'].includes(e.target.tagName.toLowerCase())) {
      // Let the default action happen
      return;
    }

    const lat = Number(store.latitude);
    const lng = Number(store.longitude);
    map.setCenter(new window.google.maps.LatLng(lat, lng));
    map.setZoom(15);

    // Remove active class from other items
    context.querySelectorAll(`.${DEFINITIONS.slug}__list-item.${DEFINITIONS.activeClass}`).forEach((item) => {
      item.classList.remove(DEFINITIONS.activeClass);
    });

    // Add active class to clicked item
    li.classList.add(DEFINITIONS.activeClass);

    // Trigger marker tooltip
    const marker = context.querySelector(`.${DEFINITIONS.slug}__marker[data-id="${store.uid || store.id}"]`);
    if (marker) {
      marker.click();
    }

    if (window.innerWidth < DEFINITIONS.breakpoints.UISwitch) {
      const mapElement = context.querySelector(`.${DEFINITIONS.slug}__map`);
      if (mapElement) mapElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  return li;
};

/**
 * @description Calculates visible range for virtual scrolling with mobile fallback
 * @param {HTMLElement} scrollContainer - Scrollable container
 * @param {number} itemCount - Total number of items
 * @returns {Object} Range object with start and end indices
 */
const calculateVisibleRange = (scrollContainer, itemCount) => {
  const { itemHeight, overscan, minVisibleItems } = virtualListState;

  if (!scrollContainer) {
    return { start: 0, end: Math.min(minVisibleItems, itemCount) };
  }

  // Get container height - try multiple methods
  let containerHeight = scrollContainer.clientHeight;

  if (containerHeight < 100) {
    const rect = scrollContainer.getBoundingClientRect();
    containerHeight = rect.height;
  }

  if (containerHeight < 100) {
    containerHeight = scrollContainer.offsetHeight;
  }

  if (containerHeight < 100) {
    containerHeight = Math.min(window.innerHeight * 0.6, 600);
  }

  const scrollTop = scrollContainer.scrollTop || 0;
  const start = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const visibleCount = Math.ceil(containerHeight / itemHeight);
  const actualVisibleCount = Math.max(visibleCount, minVisibleItems);
  const end = Math.min(itemCount, start + actualVisibleCount + overscan * 2);

  return { start, end };
};

/**
 * @description Renders visible items in the virtual list
 */
const renderVirtualList = () => {
  const { container, scrollContainer, items, context, translations, map, rendered } = virtualListState;

  if (!container) return;

  if (!items || items.length === 0) {
    console.warn('No items to render');
    // Clear container
    container.innerHTML = '';
    return;
  }

  const { start, end } = calculateVisibleRange(scrollContainer, items.length);
  virtualListState.visibleRange = { start, end };

  requestAnimationFrame(() => {
    const fragment = document.createDocumentFragment();

    if (start > 0) {
      const topSpacer = document.createElement('div');
      topSpacer.style.height = `${start * virtualListState.itemHeight}px`;
      topSpacer.className = 'virtual-list-spacer-top';
      fragment.appendChild(topSpacer);
    }

    // Render visible items
    for (let i = start; i < end; i++) {
      const store = items[i];
      if (!store) {
        console.warn('Store at index', i, 'is undefined');
        continue;
      }

      const storeId = String(store.uid || store.id);

      // Check if element exists in cache and is still valid
      let item = rendered.get(storeId);
      if (!item || item.__storeData !== store) {
        // Create new element or update if store data changed
        item = createVirtualListItem(store, translations, map, context);
        item.__storeData = store; // Store reference for comparison
        rendered.set(storeId, item);
      }

      fragment.appendChild(item);
    }

    // Add spacer for items below visible area
    if (end < items.length) {
      const bottomSpacer = document.createElement('div');
      bottomSpacer.style.height = `${(items.length - end) * virtualListState.itemHeight}px`;
      bottomSpacer.className = 'virtual-list-spacer-bottom';
      fragment.appendChild(bottomSpacer);
    }

    // Update DOM
    container.innerHTML = '';
    container.appendChild(fragment);

    // Clean up cache - remove items not in current data set
    const currentIds = new Set(items.map((s) => String(s.uid || s.id)));
    for (const [id] of rendered) {
      if (!currentIds.has(id)) {
        rendered.delete(id);
      }
    }
  });
};

/**
 * @description Gets the correct scroll container based on viewport
 * @param {HTMLElement} context - Section container
 * @returns {HTMLElement|null} The visible scroll container
 */
const getActiveScrollContainer = (context) => {
  const desktopContainer = context.querySelector(`.${DEFINITIONS.slug}__stores-container--desktop`);
  const mobileContainer = context.querySelector(`.${DEFINITIONS.slug}__stores-container--mobile`);

  if (desktopContainer && window.getComputedStyle(desktopContainer).display !== 'none') {
    return desktopContainer;
  }

  if (mobileContainer && window.getComputedStyle(mobileContainer).display !== 'none') {
    return mobileContainer;
  }

  return context.querySelector(`.${DEFINITIONS.slug}__stores-container`);
};

/**
 * @description Removes existing scroll listener
 */
const removeScrollListener = () => {
  if (virtualListState.scrollListener && virtualListState.scrollContainer) {
    virtualListState.scrollContainer.removeEventListener('scroll', virtualListState.scrollListener);
    virtualListState.scrollListener = null;
  }
};

/**
 * @description Attaches scroll listener to the current active container
 */
const attachScrollListener = () => {
  const { scrollContainer } = virtualListState;

  if (!scrollContainer) return;

  // Remove old listener first
  removeScrollListener();

  // Create new listener
  let ticking = false;
  virtualListState.scrollListener = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        renderVirtualList();
        ticking = false;
      });
      ticking = true;
    }
  };

  scrollContainer.addEventListener('scroll', virtualListState.scrollListener, { passive: true });
};

/**
 * @description Re-initializes the virtual list for the current viewport
 */
const reinitVirtualList = () => {
  if (!virtualListState.context) return;

  const { context } = virtualListState;

  // Get the new active scroll container
  const newScrollContainer = getActiveScrollContainer(context);

  // Only re-init if the container actually changed
  if (newScrollContainer !== virtualListState.scrollContainer) {
    // Remove old listener
    removeScrollListener();

    // Update scroll container reference
    virtualListState.scrollContainer = newScrollContainer;

    // Attach new listener
    attachScrollListener();

    // Re-render with new container
    renderVirtualList();
  }
};

/**
 * @description Initializes virtual list with scroll listener
 * @param {HTMLElement} context - Section container
 * @param {Array} stores - Array of store objects
 * @param {Object} translations - Translation strings
 * @param {Object} map - Google Map instance
 */
const initVirtualList = (context, stores, translations, map) => {
  const scrollContainer = getActiveScrollContainer(context);
  const listContainer = context.querySelector(`.${DEFINITIONS.slug}__list`);

  if (!listContainer) return;

  if (!scrollContainer) {
    console.warn('Virtual list: scroll container not found, will render without virtual scrolling');
  }

  virtualListState.container = listContainer;
  virtualListState.scrollContainer = scrollContainer;
  virtualListState.items = stores;
  virtualListState.context = context;
  virtualListState.translations = translations;
  virtualListState.map = map;

  // Remove existing listeners
  removeScrollListener();

  // Remove existing resize listener if any
  if (virtualListState.resizeListener) {
    window.removeEventListener('resize', virtualListState.resizeListener);
  }

  // Initial render
  const performInitialRender = () => {
    renderVirtualList();
    attachScrollListener();
  };

  // Setup resize listener with debouncing
  let resizeTimeout;
  virtualListState.resizeListener = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      reinitVirtualList();
    }, 250); // Debounce resize events
  };

  window.addEventListener('resize', virtualListState.resizeListener, { passive: true });

  // Delay initial render for layout
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      performInitialRender();
    });
  });
};

/**
 * @description Updates the store list UI using virtual scrolling for performance
 * @param {HTMLElement} context - The section container element
 * @param {Object} options - Configuration options
 * @param {Array} options.viewportStores - Currently visible stores
 * @param {Object} options.map - Google Map instance
 */
export const updateStoreListUI = (context, { viewportStores, map }) => {
  const listContainer = context.querySelector(`.${DEFINITIONS.slug}__list`);
  if (!listContainer) return;

  const translations = getTranslations(context);

  // Sort stores by distance if available
  const sortedStores = sortStoresByDistance(viewportStores || []);

  // Initialize or update virtual list
  if (!virtualListState.container) {
    initVirtualList(context, sortedStores, translations, map);
  } else {
    // Update items array
    virtualListState.items = sortedStores;
    virtualListState.context = context;
    virtualListState.translations = translations;
    virtualListState.map = map;

    // Re-render with new data
    renderVirtualList();
  }

  // Update stores count
  const storesCountElement = context.querySelector(`.${DEFINITIONS.slug}__stores-count__number--value`);
  if (storesCountElement) storesCountElement.textContent = viewportStores.length;
};
